/**
 * `peterhof_agent`'s questionnaire — a LangGraph `interrupt()` the graph opens
 * with the whole form at once (`scenario_playbook`, §plan «Interrupt/resume»),
 * stepped through here one question per turn.
 *
 * **The wire contract, fixed before this store was written:**
 * ```
 * interrupt.value = {kind:"questionnaire", scenario, title,
 *   questions:[{slot, text, options:[{id,label, input?:"date", min?, max?}], multi, allow_custom, skippable, show_if?:{slot, any_of:[optionId…]}}]}
 * resume = {status:"completed", answers:{[slot]: string[] /*ids*\/ | string /*свой текст*\/}, skipped:[slot…]}
 *        | {status:"exited", text:"что написал посетитель"}
 * ```
 * An option with `input:"date"` («Другой день», 22.09.2026) is not an answer by
 * itself: the card opens a calendar between `min` and `max` — on `default` when the
 * model proposed a day (#149) — and the picked day goes back as the
 * slot's «свой текст» — an ISO date the graph reads as the visit date. A day the
 * model proposed also comes as an ordinary option whose id IS the ISO date, marked
 * by `preset` (asst-peterhof-guide#149).
 *
 * A `multi` question with `counts: true` («кто едет» by the museum's tariffs,
 * 24.09.2026) is a stepper: a −/+ per option, and the answer is each option's
 * id ONCE PER PERSON — `["adults", "adults", "kids_7_14"]`. Still a list of
 * ids, so the wire is the same; a card that ignored the flag would send one id
 * per category and the graph would read one person in each.
 *
 * A question with `preset` (asst-peterhof-guide#112, INV-PH-57) is still asked: the
 * value is what the model took from the visitor's words — `{category: n}` on a
 * stepper, an option id on a single choice, ids on a multi — and the card opens the
 * question FILLED IN: one «Дальше» (or one tap on the marked option) confirms it, a
 * change corrects it. The graph writes only what comes back; the preset itself is
 * never an answer.
 *
 * **The pointer is a SLOT, not an index.** `show_if` can hide or reveal a
 * later question depending on an EARLIER answer, so "the third question" is
 * not a stable identity across the questionnaire's own lifetime — `visibleQuestions`
 * recomputes the eligible list from `answers` on every read, and `currentSlot`
 * is what survives that recomputation.
 *
 * **`land` is the one place a question is answered.** Every submit path
 * (`chooseSingle`, `submitMulti`, `submitCustom`, `skipCurrent`) converges on
 * it: it writes the answer (or the skip), then either advances `currentSlot`
 * to the next ELIGIBLE question or — nothing left — hands back the `resume`
 * value the caller (`App.tsx`'s `submitResume`) actually sends. That return
 * value is `null` while the questionnaire continues, which is what lets a
 * caller write `land(submitMulti())` and only act on completion.
 */

import { create } from 'zustand';

export type QuestionOption = {
  id: string;
  label: string;
  /** `'date'`: tapping it opens a calendar instead of answering. */
  input?: 'date';
  /** The calendar's bounds, ISO `YYYY-MM-DD` — only with `input: 'date'`. */
  min?: string;
  max?: string;
  /** The day the calendar opens on — the model's proposed day (#149); absent otherwise. */
  default?: string;
};

export type Question = {
  slot: string;
  text: string;
  options: QuestionOption[];
  multi?: boolean;
  allow_custom?: boolean;
  skippable?: boolean;
  show_if?: { slot: string; any_of: string[] };
  /** A stepper — see the module header. Only ever set together with `multi`. */
  counts?: boolean;
  /** The model's proposal for this question — see the module header. */
  preset?: Record<string, number> | string | string[];
};

export type QuestionnaireValue = {
  kind: 'questionnaire';
  scenario: string;
  title?: string;
  questions: Question[];
};

export type ResumeValue =
  | { status: 'completed'; answers: Record<string, string[] | string>; skipped: string[] }
  | { status: 'exited'; text: string; answers?: Record<string, string[] | string>; skipped?: string[] };

export type Active = {
  /** The paused `interrupt()`'s own id — carried for symmetry with the wire
   *  shape; resuming answers the RUN, not a specific interrupt id, so this is
   *  read only for debugging today. */
  interruptId: string;
  scenario: string;
  title?: string;
  questions: Question[];
  currentSlot: string;
  answers: Record<string, string[] | string>;
  skipped: string[];
  /** A multi-select question's in-progress picks, cleared on every step. */
  draftMulti: string[];
  /** A free-text answer's in-progress value, cleared on every step. */
  draftCustom: string;
  /** A stepper question's in-progress counts per option id, cleared on every step. */
  draftCounts: Record<string, number>;
};

export type QuestionnaireState = {
  active: Active | null;
  /** Opens (or replaces) the card — a fresh `interrupt`, or the one restored
   *  after an F5 (`GET /threads/{id}/state`'s own `interrupts[0]`). */
  open: (interruptId: string, value: QuestionnaireValue) => void;
  /** Called once the visitor's answer has actually been sent (completed or
   *  exited) — the caller owns sending it, this store owns the form state. */
  close: () => void;
  toggleMultiOption: (optionId: string) => void;
  setCustomText: (text: string) => void;
  /** A single-choice tap: answers at once and advances (or completes). */
  chooseSingle: (optionId: string) => ResumeValue | null;
  /** Commits the multi-select draft — «Дальше». Refuses an empty selection. */
  submitMulti: () => ResumeValue | null;
  /** A stepper's −/+ on one option; the count stays within 0…`COUNT_MAX`. */
  stepCount: (optionId: string, delta: number) => void;
  /** Commits the stepper draft — each id once per person. Refuses nobody. */
  submitCounts: () => ResumeValue | null;
  /** Commits the free-text draft — refuses a blank one. */
  submitCustom: () => ResumeValue | null;
  /** Only when the current question is `skippable`. */
  skipCurrent: () => ResumeValue | null;
  /** Moves to the previous ELIGIBLE question, if any. Never rewinds past the
   *  first — there is nothing to go back to. */
  back: () => void;
};

/** Whether a question with a `show_if` clause is on-screen right now. */
function isEligible(question: Question, answers: Record<string, string[] | string>): boolean {
  if (!question.show_if) return true;
  const value = answers[question.show_if.slot];
  const ids = Array.isArray(value) ? value : value !== undefined ? [value] : [];
  return question.show_if.any_of.some((id) => ids.includes(id));
}

/** The questions the visitor can actually reach, in the scenario's own order —
 *  recomputed from `answers` every time, never cached on `Active` itself, or
 *  an earlier answer changing later on `back()` could leave a stale list. */
export function visibleQuestions(active: Pick<Active, 'questions' | 'answers'>): Question[] {
  return active.questions.filter((q) => isEligible(q, active.answers));
}

/**
 * «Вопрос N из M»'s M: the reachable questions and the ones behind a `show_if` whose question is not answered
 * yet. Counted from the reachable ones only, «Вопрос 4 из 4» became «5 из 5» once «Нет, будем добираться»
 * opened «Как доберётесь» (first-user QA, dev, 21–22.10.2026): a total that falls reads as a skip, one that
 * grows reads as a broken promise.
 */
export function questionCount(active: Pick<Active, 'questions' | 'answers' | 'skipped'>): number {
  return active.questions.filter(
    (q) =>
      isEligible(q, active.answers) ||
      (q.show_if !== undefined &&
        active.answers[q.show_if.slot] === undefined &&
        !active.skipped.includes(q.show_if.slot)),
  ).length;
}

/** The drafts a question opens with: the visitor's own earlier answer when there is
 *  one (back to a question already sent — review of #112: the proposal must not
 *  silently undo a correction), else its `preset` on a stepper or a multi, else empty
 *  (a single choice shows its preset as a marked option instead). */
function draftsFor(
  question: Question | undefined,
  answered?: string[] | string,
): Pick<Active, 'draftMulti' | 'draftCustom' | 'draftCounts'> {
  const empty = { draftMulti: [], draftCustom: '', draftCounts: {} };
  if (!question) return empty;
  if (Array.isArray(answered)) {
    if (question.counts) {
      const counts: Record<string, number> = {};
      for (const id of answered) counts[id] = (counts[id] ?? 0) + 1;
      return { ...empty, draftCounts: counts };
    }
    if (question.multi) return { ...empty, draftMulti: [...answered] };
  }
  const preset = question.preset;
  return {
    draftMulti: question.multi && !question.counts && Array.isArray(preset) ? [...preset] : [],
    draftCustom: '',
    draftCounts:
      question.counts && preset && typeof preset === 'object' && !Array.isArray(preset) ? { ...preset } : {},
  };
}

/** Is *optionId* the one a single-choice question should mark — the visitor's own
 *  earlier answer, else the model's proposal? `QuestionCard` reads it. */
export function markedOption(question: Question, answered?: string[] | string): string | undefined {
  if (typeof answered === 'string') return answered;
  return typeof question.preset === 'string' ? question.preset : undefined;
}

function questionOf(active: Pick<Active, 'questions'>, slot: string): Question | undefined {
  return active.questions.find((q) => q.slot === slot);
}

function slotAfter(active: Pick<Active, 'questions' | 'answers'>, slot: string): string | null {
  const visible = visibleQuestions(active);
  const at = visible.findIndex((q) => q.slot === slot);
  return visible[at + 1]?.slot ?? null;
}

export const useQuestionnaire = create<QuestionnaireState>((set, get) => {
  /** Writes `next` and either steps to the following eligible question or
   *  reports completion — the one function every submit path lands on. */
  function land(next: Active): ResumeValue | null {
    const following = slotAfter(next, next.currentSlot);
    if (following) {
      set({ active: { ...next, currentSlot: following, ...draftsFor(questionOf(next, following)) } });
      return null;
    }
    set({ active: next });
    return { status: 'completed', answers: next.answers, skipped: next.skipped };
  }

  return {
    active: null,

    open: (interruptId, value) => {
      const first = value.questions[0];
      // the same paused card after an F5: where the visitor was, with what they had answered
      const saved = readProgress(interruptId);
      const resumed = saved ? questionOf(value, saved.currentSlot) : undefined;
      const current = resumed ?? first;
      const answers = resumed ? saved!.answers : {};
      set({
        active: current
          ? {
              interruptId,
              scenario: value.scenario,
              title: value.title,
              questions: value.questions,
              currentSlot: current.slot,
              answers,
              skipped: resumed ? saved!.skipped : [],
              ...draftsFor(current, answers[current.slot]),
            }
          : null,
      });
    },

    close: () => set({ active: null }),

    toggleMultiOption: (optionId) =>
      set((state) => {
        if (!state.active) return state;
        const draftMulti = state.active.draftMulti.includes(optionId)
          ? state.active.draftMulti.filter((id) => id !== optionId)
          : [...state.active.draftMulti, optionId];
        return { active: { ...state.active, draftMulti } };
      }),

    stepCount: (optionId, delta) =>
      set((state) => {
        if (!state.active) return state;
        const now = state.active.draftCounts[optionId] ?? 0;
        const next = Math.max(0, Math.min(COUNT_MAX, now + delta));
        return { active: { ...state.active, draftCounts: { ...state.active.draftCounts, [optionId]: next } } };
      }),

    submitCounts: () => {
      const active = get().active;
      if (!active) return null;
      const question = active.questions.find((q) => q.slot === active.currentSlot);
      // In the question's own option order, so the answer reads the way the card did.
      const ids = (question?.options ?? []).flatMap((o) =>
        Array.from({ length: active.draftCounts[o.id] ?? 0 }, () => o.id),
      );
      if (ids.length === 0) return null;
      return land({
        ...active,
        answers: { ...active.answers, [active.currentSlot]: ids },
        draftMulti: [],
        draftCustom: '',
        draftCounts: {},
      });
    },

    setCustomText: (text) =>
      set((state) => (state.active ? { active: { ...state.active, draftCustom: text } } : state)),

    chooseSingle: (optionId) => {
      const active = get().active;
      if (!active) return null;
      return land({
        ...active,
        answers: { ...active.answers, [active.currentSlot]: optionId },
        draftMulti: [],
        draftCustom: '',
        draftCounts: {},
      });
    },

    submitMulti: () => {
      const active = get().active;
      if (!active || active.draftMulti.length === 0) return null;
      return land({
        ...active,
        answers: { ...active.answers, [active.currentSlot]: [...active.draftMulti] },
        draftMulti: [],
        draftCustom: '',
        draftCounts: {},
      });
    },

    submitCustom: () => {
      const active = get().active;
      if (!active) return null;
      const text = active.draftCustom.trim();
      if (!text) return null;
      return land({
        ...active,
        answers: { ...active.answers, [active.currentSlot]: text },
        draftMulti: [],
        draftCustom: '',
        draftCounts: {},
      });
    },

    skipCurrent: () => {
      const active = get().active;
      if (!active) return null;
      const current = active.questions.find((q) => q.slot === active.currentSlot);
      if (!current?.skippable) return null;
      return land({
        ...active,
        skipped: [...active.skipped, active.currentSlot],
        draftMulti: [],
        draftCustom: '',
        draftCounts: {},
      });
    },

    back: () =>
      set((state) => {
        if (!state.active) return state;
        const visible = visibleQuestions(state.active);
        const at = visible.findIndex((q) => q.slot === state.active!.currentSlot);
        const prev = visible[at - 1];
        if (!prev) return state;
        return {
          active: { ...state.active, currentSlot: prev.slot, ...draftsFor(prev, state.active.answers[prev.slot]) },
        };
      }),
  };
});

/**
 * The card's progress across a reload: F5 on question 2 of 5 put the visitor back on question 1 with the date
 * they had answered gone (first-user QA, dev, 21.10.2026). Per tab (`sessionStorage`), keyed by the paused
 * interrupt — another questionnaire starts clean; no storage, and the card simply starts over.
 */
const PROGRESS_KEY = 'ph.questionnaireProgress';
type Progress = Pick<Active, 'interruptId' | 'currentSlot' | 'answers' | 'skipped'>;

export function readProgress(interruptId: string): Progress | null {
  try {
    const raw = sessionStorage.getItem(PROGRESS_KEY);
    const saved = raw ? (JSON.parse(raw) as Progress) : null;
    return saved && saved.interruptId === interruptId && typeof saved.currentSlot === 'string' ? saved : null;
  } catch {
    return null;
  }
}

function writeProgress(active: Active | null): void {
  try {
    if (!active) sessionStorage.removeItem(PROGRESS_KEY);
    else {
      const { interruptId, currentSlot, answers, skipped } = active;
      sessionStorage.setItem(PROGRESS_KEY, JSON.stringify({ interruptId, currentSlot, answers, skipped }));
    }
  } catch {
    /* no storage — nothing to restore after a reload */
  }
}

useQuestionnaire.subscribe((state, previous) => {
  if (state.active !== previous.active) writeProgress(state.active);
});

/** The `resume` a visitor's typed composer text sends — while a questionnaire
 *  is active, ordinary free text exits it rather than starting a new turn
 *  (`App.tsx`'s `enqueueOrSend`). */
export function exitedResume(
  text: string,
  answers?: Record<string, string[] | string>,
  skipped: string[] = [],
): ResumeValue {
  // the questions already answered go with the exit: «Сегодня» tapped, then «весь день, мы с ребёнком» typed — the day
  // was asked again (QA dev 30.10.2026, С3-3); an answer the visitor skipped after giving it does not (Codex review)
  const given = Object.fromEntries(Object.entries(answers ?? {}).filter(([slot]) => !skipped.includes(slot)));
  return Object.keys(given).length ? { status: 'exited', text, answers: given, skipped } : { status: 'exited', text };
}

/** A stepper's ceiling per option — the graph caps a category at 30 too. */
export const COUNT_MAX = 30;

const SLOT_LABELS: Record<string, string> = {
  when: 'Когда',
  duration: 'Сколько времени',
  group: 'Кто едет',
  // the older person the model read from the visitor's words (#121, asked only then)
  seniors: 'Пожилой в составе',
  pace: 'Темп',
  party: 'Кто едет',
  kid_age: 'Возраст ребёнка',
  transfer: 'Как добираетесь',
  // v2's «Вы уже в Петергофе?» (`graphs/peterhof_v2/executors.py`, ON_SITE) — read «on_site: Нет, будем
  // добираться» in the visitor's own bubble (first-user QA, dev, 21.10.2026)
  on_site: 'Уже в Петергофе',
  interest: 'Что интереснее',
  // the «забудь меня» card (`graphs/peterhof_plan/tools/forget.py`, #90)
  forget: 'Стереть память',
};

/** A short, human label for one slot — the summary bubble reads
 *  «Когда: …», never the slot's own machine name, when it knows one. Falls
 *  back to the slot id itself for a scenario this package does not know
 *  about yet, which is still more useful than dropping the line. */
function slotLabel(slot: string): string {
  return SLOT_LABELS[slot] ?? slot;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

/** «2026-10-03» → «3 октября» — how a calendar pick reads in the visitor's
 *  own bubble; anything else is returned as it was typed. */
export function sayDate(value: string): string {
  const match = ISO_DATE.exec(value);
  if (!match) return value;
  const month = MONTHS_GENITIVE[Number(match[2]) - 1];
  return month ? `${Number(match[3])} ${month}` : value;
}

/**
 * The one visitor bubble a completed questionnaire ends on — «Когда: в
 * выходные · Сколько времени: полдня · …» — with every option id resolved to
 * its own LABEL rather than echoed back as an id nobody typed. Reads
 * `answers` off the `ResumeValue` that just completed (not `active.answers`,
 * which the caller has typically already cleared by the time it calls this).
 */
export function summarizeAnswers(
  questions: readonly Question[],
  answers: Record<string, string[] | string>,
): string {
  const parts: string[] = [];
  for (const question of questions) {
    const value = answers[question.slot];
    if (value === undefined) continue;
    const label = answerLabel(question, value);
    if (label) parts.push(`${slotLabel(question.slot)}: ${label}`);
  }
  return parts.join(' · ');
}

/** A questionnaire answer as the visitor would say it in the chat transcript. */
export function answerLabel(question: Question, value: string[] | string): string {
  return Array.isArray(value)
    ? question.counts
      ? countedLabel(question, value)
      : value.map((id) => question.options.find((o) => o.id === id)?.label ?? id).join(', ')
    : (question.options.find((o) => o.id === value)?.label ?? sayDate(value));
}

/** «Взрослые (граждане РФ и ЕАЭС) × 2, Дети 7–14 лет × 1» — a stepper's answer. */
function countedLabel(question: Question, ids: readonly string[]): string {
  return question.options
    .map((o) => ({ label: o.label, n: ids.filter((id) => id === o.id).length }))
    .filter(({ n }) => n > 0)
    .map(({ label, n }) => `${label} × ${n}`)
    .join(', ');
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function readOption(raw: unknown): QuestionOption | null {
  if (!isObject(raw)) return null;
  const id = typeof raw.id === 'string' ? raw.id : '';
  const label = typeof raw.label === 'string' ? raw.label : '';
  if (!id || !label) return null;
  if (raw.input !== 'date') return { id, label };
  return {
    id,
    label,
    input: 'date',
    ...(typeof raw.min === 'string' ? { min: raw.min } : {}),
    ...(typeof raw.max === 'string' ? { max: raw.max } : {}),
    ...(typeof raw.default === 'string' && ISO_DATE.test(raw.default) ? { default: raw.default } : {}),
  };
}

function readQuestion(raw: unknown): Question | null {
  if (!isObject(raw)) return null;
  const slot = typeof raw.slot === 'string' ? raw.slot : '';
  const text = typeof raw.text === 'string' ? raw.text : '';
  if (!slot || !text) return null;
  const options = Array.isArray(raw.options)
    ? raw.options.map(readOption).filter((o): o is QuestionOption => o !== null)
    : [];
  const showIfRaw = raw.show_if;
  const show_if =
    isObject(showIfRaw) && typeof showIfRaw.slot === 'string' && Array.isArray(showIfRaw.any_of)
      ? { slot: showIfRaw.slot, any_of: showIfRaw.any_of.filter((v): v is string => typeof v === 'string') }
      : undefined;
  const multi = raw.multi === true;
  const counts = raw.counts === true && multi;
  const preset = readPreset(raw.preset, options, multi, counts);
  return {
    slot,
    text,
    options,
    multi,
    allow_custom: raw.allow_custom === true,
    skippable: raw.skippable === true,
    ...(show_if ? { show_if } : {}),
    ...(counts ? { counts: true } : {}),
    ...(preset !== undefined ? { preset } : {}),
  };
}

/**
 * A question's `preset`, kept only in the shape its own answer has and only with
 * ids the question offers — a stepper: whole counts within 0…`COUNT_MAX`; a single
 * choice: one ordinary option (never the calendar); a multi: a list. Anything else is
 * dropped and the question opens empty, as it did before the field existed.
 */
function readPreset(
  raw: unknown,
  options: readonly QuestionOption[],
  multi: boolean,
  counts: boolean,
): Question['preset'] | undefined {
  const ids = new Set(options.filter((o) => o.input !== 'date').map((o) => o.id));
  if (counts) {
    if (!isObject(raw)) return undefined;
    const out: Record<string, number> = {};
    for (const [id, n] of Object.entries(raw)) {
      if (ids.has(id) && typeof n === 'number' && Number.isInteger(n) && n > 0) out[id] = Math.min(COUNT_MAX, n);
    }
    return Object.keys(out).length ? out : undefined;
  }
  if (multi) {
    const list = Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string' && ids.has(v)) : [];
    return list.length ? list : undefined;
  }
  return typeof raw === 'string' && ids.has(raw) ? raw : undefined;
}

/**
 * Parses an `interrupt.value` as a questionnaire, or `null` for anything else
 * — an unrecognised `kind`, or a malformed one (no questions left after
 * filtering). Callers log and ignore a `null`, per the contract: "неизвестный
 * kind interrupt'а — игнорировать с console.warn, не падать".
 */
export function readQuestionnaireValue(raw: unknown): QuestionnaireValue | null {
  if (!isObject(raw) || raw.kind !== 'questionnaire') return null;
  const scenario = typeof raw.scenario === 'string' ? raw.scenario : '';
  const title = typeof raw.title === 'string' ? raw.title : undefined;
  const questions = Array.isArray(raw.questions)
    ? raw.questions.map(readQuestion).filter((q): q is Question => q !== null)
    : [];
  if (questions.length === 0) return null;
  return { kind: 'questionnaire', scenario, ...(title ? { title } : {}), questions };
}
